/**
 * ehr#681 (Taras, 10-29): the client drawer's save timing.
 * - A save shows "Saving…" on the clicked button for at least SAVE_MIN_MS, even when the server
 *   answers sooner, so the person sees it happen; a slower save keeps it until the answer.
 * - "Record last saved 5 minutes ago" / "Draft last saved just now" under the save buttons.
 */

export const SAVE_MIN_MS = 2000;
/** How often the "last saved" line refreshes its "X ago". */
export const LAST_SAVED_TICK_MS = 15000;

/** Settles like `p`, but never sooner than `ms` after the call. */
export function atLeast<T>(p: Promise<T>, ms: number): Promise<T> {
  const wait = new Promise<void>((r) => setTimeout(r, ms));
  return Promise.allSettled([p, wait]).then(([res]) => {
    if (res.status === 'fulfilled') return res.value;
    throw res.reason;
  });
}

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'} ago`;

/** "just now" under 10 s, then "N seconds / minutes / hours / days ago". */
export function savedAgo(at: number, now: number): string {
  const s = Math.max(0, Math.floor((now - at) / 1000));
  if (s < 10) return 'just now';
  if (s < 60) return plural(s, 'second');
  const m = Math.floor(s / 60);
  if (m < 60) return plural(m, 'minute');
  const h = Math.floor(m / 60);
  if (h < 24) return plural(h, 'hour');
  return plural(Math.floor(h / 24), 'day');
}

export type LastSaved = { kind: 'draft' | 'record'; at: number };

/** Any screen's line: "Follow-up last saved 5 minutes ago". */
export function savedLine(what: string, at: number, now: number): string {
  return `${what} last saved ${savedAgo(at, now)}`;
}

export function lastSavedLine(last: LastSaved, now: number): string {
  return `${last.kind === 'draft' ? 'Draft' : 'Record'} last saved ${savedAgo(last.at, now)}`;
}

/** A successful save moves the line to now; a failed one keeps the previous time. */
export function afterSave(
  prev: LastSaved | null,
  ok: boolean,
  draft: boolean,
  now: number,
): LastSaved | null {
  return ok ? { kind: draft ? 'draft' : 'record', at: now } : prev;
}

/**
 * The time the server already has, from GET demographics: `_savedAt` (the latest
 * contact.demographics.update in the audit trail) and the stored `_status` of that save. Only the
 * latest save is known, so a chart last saved as a draft shows the draft line only.
 */
export function lastSavedFromServer(raw: unknown): LastSaved | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as { _savedAt?: unknown; _status?: unknown };
  const at = typeof r._savedAt === 'string' ? Date.parse(r._savedAt) : NaN;
  if (!Number.isFinite(at)) return null;
  return { kind: r._status === 'draft' ? 'draft' : 'record', at };
}
